import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, Clock, Users, Trash2 } from 'lucide-react';
import { categoryIcons } from './RecipePlaceholder';
import { recipes as recipeApi } from '../utils/api';

const RecipeListItem = ({ recipe, onClick, userLabels = [], hideCategory = false }) => {
  const config = categoryIcons[recipe.category] || categoryIcons.other;
  const { Icon } = config;

  const hasImage = recipe.has_image || (recipe.images && recipe.images.length > 0) || !!recipe.image_url;

  const initialThumbnail = (recipe.images && recipe.images.length > 0)
    ? (recipe.images[0].startsWith('data:') || recipe.images[0].startsWith('http') ? recipe.images[0] : `data:image/jpeg;base64,${recipe.images[0]}`)
    : recipe.image_url || null;

  const [thumbnail, setThumbnail] = useState(initialThumbnail);
  const [loadingThumb, setLoadingThumb] = useState(false);

  useEffect(() => {
    if (thumbnail || !recipe.has_image) return;
    let cancelled = false;
    const loadThumbnail = async () => {
      try {
        const res = await recipeApi.getThumbnail(recipe.recipe_id);
        if (!cancelled && res.data?.thumbnail) setThumbnail(res.data.thumbnail);
      } catch (err) {
        console.error('Failed to load thumbnail:', err);
      } finally {
        if (!cancelled) setLoadingThumb(false);
      }
    };
    setLoadingThumb(true);
    loadThumbnail();
    return () => { cancelled = true; };
  }, [recipe.recipe_id, recipe.has_image, thumbnail]);

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center gap-0.5">
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={12} className={i < rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'} />
        ))}
      </div>
    );
  };

  const getLabelInfo = (labelName) => {
    const label = userLabels.find(l => l.name === labelName);
    return label || { name: labelName, color: '#6b7280', icon: null };
  };

  return (
    <div
      onClick={onClick}
      data-testid={`recipe-list-item-${recipe.recipe_id}`}
      className={`flex items-center gap-3.5 py-3.5 px-4 cursor-pointer hover:bg-gray-50 transition-colors ${
        recipe.deleted ? 'opacity-50' : ''
      }`}
    >
      {/* Thumbnail — 88x88, rounded-xl */}
      <div className="flex-shrink-0 w-[88px] h-[88px] rounded-xl overflow-hidden">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt={recipe.title}
            className={`w-full h-full object-cover ${recipe.deleted ? 'grayscale' : ''}`}
          />
        ) : hasImage && loadingThumb ? (
          <div className="w-full h-full bg-gray-100 flex items-center justify-center">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className={`w-full h-full bg-gray-100 flex items-center justify-center ${recipe.deleted ? 'grayscale' : ''}`}>
            <Icon size={28} className="text-gray-500" strokeWidth={1.5} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-gray-900 line-clamp-2">{recipe.title}</h3>
          {recipe.deleted && (
            <span className="flex items-center gap-1 text-xs text-red-400 bg-red-50 px-2 py-0.5 rounded">
              <Trash2 size={10} /> Deleted
            </span>
          )}
          {recipe.cooked && !recipe.deleted && (
            <span className="flex items-center gap-1 text-xs text-emerald-600">
              <CheckCircle size={12} /> Cooked
            </span>
          )}
        </div>

        {/* Category & Labels */}
        {(recipe.labels?.length > 0 || !hideCategory) && (
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            {!hideCategory && (
              <span className="text-xs text-gray-500 font-medium">{config.label}</span>
            )}
            {recipe.labels && recipe.labels.length > 0 && (
              <>
                {recipe.labels.filter(l => l !== 'Up Next').slice(0, 3).map((labelName) => {
                  const labelInfo = getLabelInfo(labelName);
                  return (
                    <span
                      key={labelName}
                      className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium text-white"
                      style={{ backgroundColor: labelInfo.color }}
                    >
                      {labelName}
                    </span>
                  );
                })}
                {recipe.labels.filter(l => l !== 'Up Next').length > 3 && (
                  <span className="text-xs text-gray-500">+{recipe.labels.filter(l => l !== 'Up Next').length - 3}</span>
                )}
              </>
            )}
          </div>
        )}

        {/* Meta: time, servings */}
        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
          {(recipe.prep_time || recipe.cook_time) && (
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {(recipe.prep_time || 0) + (recipe.cook_time || 0)} min
            </span>
          )}
          {recipe.servings > 0 && (
            <span className="flex items-center gap-1">
              <Users size={12} />
              {recipe.servings}
            </span>
          )}
        </div>
      </div>

      {/* Rating */}
      <div className="flex-shrink-0">
        {renderStars(recipe.rating)}
      </div>
    </div>
  );
};

export default RecipeListItem;
